import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { Steps } from "primereact/steps";
import { theme } from "styles/theme";
import { useResponsive } from "hooks/useResponsive";
import {
  useAddDeviceWizard,
} from "hooks/useAddDeviceWizard";
import { validateSerialNumber } from "./add-device/wizardValidation";
import "styles/components/addDeviceWizard.css";
import { WizardStepScan } from "./add-device/WizardStepScan";
import { WizardStepConnect } from "./add-device/WizardStepConnect";
import { WizardStepWifi } from "./add-device/WizardStepWifi";
import { WizardStepLink } from "./add-device/WizardStepLink";
import { WizardStepFinish } from "./add-device/WizardStepFinish";

interface AddDeviceWizardProps {
  visible: boolean;
  onHide: () => void;
  onComplete: (deviceName: string | undefined) => void;
}

const wizardSteps = [
  { label: "Scan" },
  { label: "Connect" },
  { label: "Configure WiFi" },
  { label: "Link Device" },
  { label: "Finish" },
];

const AddDeviceWizard = ({
  visible,
  onHide,
  onComplete,
}: AddDeviceWizardProps) => {
  const { isMobile, isTablet } = useResponsive();
  const wizard = useAddDeviceWizard({ onComplete, onHide });

  const renderStepContent = () => {
    switch (wizard.activeStep) {
      case 0:
        return (
          <WizardStepScan
            processing={wizard.processing}
            loading={wizard.loading}
            onScan={wizard.handleScan}
            onSkipScan={wizard.handleSkipScan}
          />
        );
      case 1:
        return (
          <WizardStepConnect
            deviceName={wizard.deviceDisplayName}
            processing={wizard.processing}
            loading={wizard.loading}
            isConnected={wizard.isConnected}
            onConnect={wizard.handleConnect}
            onContinue={() => wizard.setActiveStep(2)}
            onBack={() => wizard.setActiveStep(0)}
          />
        );
      case 2:
        return (
          <WizardStepWifi
            wifiSSID={wizard.wifiSSID}
            wifiPassword={wizard.wifiPassword}
            processing={wizard.processing}
            loading={wizard.loading}
            onSSIDChange={wizard.setWifiSSID}
            onPasswordChange={wizard.setWifiPassword}
            onConfigure={wizard.handleConfigureWifi}
            onBack={() => wizard.setActiveStep(1)}
          />
        );
      case 3:
        return (
          <WizardStepLink
            skipScan={wizard.skipScan}
            deviceSerialNumber={wizard.deviceSerialNumber}
            clientEmail={wizard.clientEmail}
            isEmailValid={wizard.isEmailValid}
            isLinking={wizard.createDeviceLinkMutation.isPending}
            onSerialChange={wizard.handleSerialNumberChange}
            onEmailChange={wizard.handleEmailChange}
            onLink={wizard.handleLinkDevice}
            onBack={() => wizard.setActiveStep(wizard.skipScan ? 0 : 2)}
            validateSerialNumber={validateSerialNumber}
          />
        );
      case 4:
        return (
          <WizardStepFinish
            wifiSSID={wizard.wifiSSID}
            onFinish={wizard.handleFinish}
          />
        );
      default:
        return null;
    }
  };

  const isBusy =
    wizard.processing || wizard.createDeviceLinkMutation.isPending;

  return (
    <Dialog
      header="Add New Device"
      visible={visible}
      style={{ width: isMobile ? "95vw" : isTablet ? "80vw" : "50vw" }}
      onHide={onHide}
      closable={!isBusy}
      closeOnEscape={!isBusy}
      modal
      className="add-device-wizard-dialog"
    >
      <Card className="p-mb-4">
        <Steps
          model={wizardSteps}
          activeIndex={wizard.activeStep}
          readOnly
          className={isMobile ? "p-steps-mobile" : ""}
        />
      </Card>

      {isBusy && (
        <div className="p-mb-4">
          <ProgressBar mode="indeterminate" color={theme.colors.primary} />
          <p className="p-text-center">{wizard.statusMessage}</p>
        </div>
      )}

      <Card>{renderStepContent()}</Card>
    </Dialog>
  );
};

export default AddDeviceWizard;
